// ModalAsignarTags.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  Divider,
  Pagination
} from "@mui/material";
import Swal from "sweetalert2";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.hmsaa.com.mx:5001";
const PAGE_SIZE = 10;

export default function ModalAsignarTags({ open, onClose, idProducto, onTagSaved }) {
  const [tagsSistema, setTagsSistema] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);

  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroDepto, setFiltroDepto] = useState("");
  const [page, setPage] = useState(1);

  const [selectedTag, setSelectedTag] = useState(null);
  const [loading, setLoading] = useState(false);

  // ==========================================================
  // LOAD DATA
  // ==========================================================
  useEffect(() => {
    if (!open) return;

    setSelectedTag(null);
    setFiltroTexto("");
    setFiltroDepto("");
    setPage(1);

    fetch(`${API_BASE}/api/tags?disponibles=true`)
      .then(r => r.json())
      .then(data => setTagsSistema(data || []));

    fetch(`${API_BASE}/api/departamentos`)
      .then(r => r.json())
      .then(data => setDepartamentos(data || []));
  }, [open]);

  // ==========================================================
  // FILTRO UNIFICADO
  // ==========================================================
  const tagsFiltrados = useMemo(() => {
    return tagsSistema.filter(t => {
      if (t.asignado) return false;

      if (filtroDepto && t.departamento !== filtroDepto) return false;

      if (filtroTexto) {
        const txt = filtroTexto.toLowerCase();
        const codigo = t.codigo.toLowerCase();
        const tipo = codigo.split("-")[0]; // TI, S, etc.

        if (!codigo.includes(txt) && !tipo.includes(txt)) return false;
      }

      return true;
    });
  }, [tagsSistema, filtroTexto, filtroDepto]);

  // ==========================================================
  // PAGINACIÓN
  // ==========================================================
  const totalPages = Math.ceil(tagsFiltrados.length / PAGE_SIZE);

  const tagsPagina = tagsFiltrados.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // ==========================================================
  // ASIGNAR TAG
  // ==========================================================
  const asignarTag = async () => {
    if (!selectedTag) return;

    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/productos/${idProducto}/tags`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_tag: selectedTag.id_tag })
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Error asignando tag");

      Swal.fire("Éxito", "Tag asignado correctamente", "success");
      await onTagSaved?.();
      onClose();
    } catch (e) {
      Swal.fire("Error", e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Asignar Tag</DialogTitle>

      <DialogContent dividers>
        {/* FILTROS */}
        <Box display="flex" gap={1} mb={2}>
          <TextField
            label="Buscar por código o tipo (ej. TI)"
            value={filtroTexto}
            onChange={e => {
              setFiltroTexto(e.target.value);
              setPage(1);
            }}
            fullWidth
            size="small"
          />

          <TextField
            select
            size="small"
            label="Departamento"
            value={filtroDepto}
            onChange={e => {
              setFiltroDepto(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {departamentos.map(d => (
              <MenuItem key={d.id_departamento} value={d.nombre}>
                {d.nombre}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* LISTADO */}
        <Box border="1px solid #eee" borderRadius={1}>
          {tagsPagina.map(tag => {
            const selected = selectedTag?.id_tag === tag.id_tag;

            return (
              <Box
                key={tag.id_tag}
                p={1.2}
                sx={{
                  cursor: "pointer",
                  backgroundColor: selected ? "#e3f2fd" : "transparent",
                  "&:hover": { backgroundColor: "#f5f7fa" }
                }}
                onClick={() => setSelectedTag(tag)}
              >
                <Typography variant="body2">
                  <b>{tag.codigo}</b> — {tag.departamento}
                </Typography>
              </Box>
            );
          })}

          {tagsPagina.length === 0 && (
            <Typography p={2} align="center" color="text.secondary">
              No hay tags disponibles
            </Typography>
          )}
        </Box>

        {/* PAGINACIÓN */}
        {totalPages > 1 && (
          <Box mt={2} display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
              size="small"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>

        <Button
          variant="contained"
          color="success"
          disabled={!selectedTag || loading}
          onClick={asignarTag}
        >
          Asignar Tag
        </Button>
      </DialogActions>
    </Dialog>
  );
}
